import { useApp } from "context/AppContext";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAnswerData, getQuestionnaireData } from "../Api";
import useConditionalQuestionnaire from "../useConditionalQuestionnaire";

type QuestionnaireContextValues = {
	questions: (PopulatedQuestion & { answer: TAnswer | undefined })[];
	completionPercentage: number;
	isLoading: boolean;
	hasUnansweredQuestions: boolean;
	isFormCompleted: [boolean, boolean];
	isSidebarOpen: boolean;
	isCoTravelerModalOpen: boolean;
	activeTab: "questionnaire" | "document";

	updateAnswerByVisaQuestionId: (visaQuestionId: string, answer: TAnswer) => void;
	setIsSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
	setIsCoTravelerModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
	setIsFormCompleted: React.Dispatch<React.SetStateAction<[boolean, boolean]>>;
	setActiveTab: React.Dispatch<React.SetStateAction<"questionnaire" | "document">>;
};

const QuestionnaireContext = createContext<QuestionnaireContextValues | undefined>(undefined);

const QuestionnaireProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
	const { getOrder, getCoTravelers, applicationId } = useApp();
	const [isLoading, setIsLoading] = useState(false);
	const [questions, setQuestions] = useState<(PopulatedQuestion & { answer: TAnswer | undefined })[]>([]);
	const [isFormCompleted, setIsFormCompleted] = useState<[boolean, boolean]>([false, false]); // [questionnaire, document]
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);
	const [isCoTravelerModalOpen, setIsCoTravelerModalOpen] = useState(false);
	const [hasUnansweredQuestions, setHasUnansweredQuestions] = useState(false);
	const [activeTab, setActiveTab] = useState<"questionnaire" | "document">("questionnaire");

	const navigate = useNavigate();
	const { orderId } = useParams();

	const { getUpdatedAllQuestionsBasedOnRenders } = useConditionalQuestionnaire({
		questions
	});

	const updateAnswerByVisaQuestionId = (visaQuestionId: string, answer: TAnswer) => {
		const questions = getUpdatedAllQuestionsBasedOnRenders(visaQuestionId, answer.answer);
		setQuestions(
			questions.map((question) =>
				question._id === visaQuestionId ? { ...question, answer: { ...question.answer, ...answer } } : question
			)
		);
	};

	useEffect(() => {
		if (!orderId) {
			navigate(`/notfound`);
			return;
		}
		getOrder(orderId);
		getCoTravelers(orderId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [navigate, orderId]);

	useEffect(() => {
		if (!orderId) {
			navigate(`/notfound`);
			return;
		}

		if (!applicationId) return;

		const fetch = async () => {
			try {
				setIsLoading(true);
				setQuestions([]);
				setHasUnansweredQuestions(false);
				const promises = [getQuestionnaireData(), getAnswerData(applicationId)] as const;
				const [questionnaireData, answerData] = await Promise.all(promises);
				const answerMap = answerData.reduce((prev, curr) => {
					prev[curr.visa_questions_id] = curr;
					return prev;
				}, {} as Record<string, TAnswer>);
				const questionsWithAnswer: (PopulatedQuestion & { answer: TAnswer | undefined })[] =
					questionnaireData.populatedQuestions
						.map((que): PopulatedQuestion & { answer: TAnswer | undefined } => ({
							...que,
							answer: answerMap[que._id]
						}))
						.sort((a, b) => a.question_rank - b.question_rank);
				setHasUnansweredQuestions(
					questionsWithAnswer.some(
						(que) => !que.answer || que.answer?.status === "UNSTARTED" || que.answer?.status === "REJECTED"
					)
				);

				setQuestions(questionsWithAnswer);
			} catch (error) {
			} finally {
				setIsLoading(false);
			}
		};

		fetch();
	}, [navigate, orderId, getOrder, applicationId]);

	const visibleQuestions = useMemo(() => {
		let updated = questions.slice();

		for (const q of questions) {
			if (q.is_root && q.answer?.answer) {
				updated = getUpdatedAllQuestionsBasedOnRenders(q._id, q.answer.answer);
			}
		}

		return updated.filter((q) => {
			if (activeTab === "questionnaire") return q.type !== "DOCUMENT" && q.answer_source === "USER_INPUT";
			else return q.type === "DOCUMENT" && q.answer_source === "USER_INPUT";
		});
	}, [questions, activeTab]);

	const completionPercentage = useMemo(() => {
		const total = visibleQuestions.length;
		const completed = visibleQuestions.filter(
			(que) =>
				que.answer?.status === "ANSWERED" || que.answer?.status === "APPROVED" || que.answer?.status === "NA"
		).length;
		return !total ? 0 : (completed / total) * 100;
	}, [visibleQuestions]);

	return (
		<QuestionnaireContext.Provider
			value={{
				questions: visibleQuestions,
				completionPercentage,
				isLoading,
				hasUnansweredQuestions,
				isFormCompleted,
				isSidebarOpen,
				isCoTravelerModalOpen,
				activeTab,

				updateAnswerByVisaQuestionId,
				setIsSidebarOpen,
				setIsCoTravelerModalOpen,
				setIsFormCompleted,
				setActiveTab
			}}>
			{children}
		</QuestionnaireContext.Provider>
	);
};
export const useQuestionnaire = () => {
	const context = useContext(QuestionnaireContext);

	if (!context) {
		throw new Error("Cannot use useQuestionnaire outside the Provider");
	}

	return context;
};

export default QuestionnaireProvider;

